/**
 * Contextual AI tip engine: 12 conditions evaluated daily per business.
 * Used by frontend for TIP_LINKS and plan visibility; Edge Function has its own copy for check/buildContext/buildPrompt.
 */

export interface TipContext {
  conditionId: string;
  numbers: Record<string, string | number>;
  productName?: string;
}

export interface BusinessSnapshot {
  businessName: string;
  currency: string;
  products: {
    id: string;
    name: string;
    category: string;
    stock: number;
    minThreshold: number;
    buyingPrice: number;
    sellingPrice: number;
    margin: number;
    salesLast30: number;
    salesPrev30: number;
    lastSaleDate: string | null;
  }[];
  salesThisWeek: number;
  salesPrevWeek: number;
  salesThisMonth: number;
  salesPrevMonth: number;
  expensesThisMonth: number;
  expensesPrevMonth: number;
  expensesByCategory: { category: string; amount: number }[];
  deadStockItems: { name: string; value: number; daysSinceLastSale: number }[];
  deadStockValueThisMonth: number;
  deadStockValuePrevMonth: number;
  slowMovers: { name: string; unitsSold: number }[];
  grossMarginThisMonth: number;
  grossMarginPrevMonth: number;
  cashRunwayDays: number;
  totalInventoryValue: number;
  revenueByProduct: { name: string; revenue: number; pct: number }[];
  plan: "free" | "pro_solo" | "pro_growth";
}

export interface TipCondition {
  id: string;
  priority: number;
  cooldown_days: number;
  plans: ("free" | "pro_solo" | "pro_growth")[];
  check: (data: BusinessSnapshot) => boolean;
  buildContext: (data: BusinessSnapshot) => TipContext;
  buildPrompt: (ctx: TipContext) => string;
}

export const TIP_CONDITIONS: TipCondition[] = [
  {
    id: "dead_stock_rising",
    priority: 1,
    cooldown_days: 7,
    plans: ["free", "pro_solo", "pro_growth"],
    check: (d) =>
      d.deadStockValueThisMonth > d.deadStockValuePrevMonth * 1.2 && d.deadStockItems.length > 0,
    buildContext: (d) => ({
      conditionId: "dead_stock_rising",
      numbers: {
        pctIncrease: Math.round(
          ((d.deadStockValueThisMonth - d.deadStockValuePrevMonth) / d.deadStockValuePrevMonth) *
            100
        ),
        totalValue: d.deadStockValueThisMonth,
        topItem: d.deadStockItems[0]?.name,
        topItemValue: d.deadStockItems[0]?.value,
        topItemDays: d.deadStockItems[0]?.daysSinceLastSale,
      },
      productName: d.deadStockItems[0]?.name,
    }),
    buildPrompt: (ctx) =>
      `Dead stock value increased ${ctx.numbers.pctIncrease}% this month. The main dead stock item is ${ctx.numbers.topItem} worth K ${ctx.numbers.topItemValue} with no sales in ${ctx.numbers.topItemDays} days. Write one recovery tip.`,
  },
  {
    id: "revenue_drop_sharp",
    priority: 1,
    cooldown_days: 3,
    plans: ["free", "pro_solo", "pro_growth"],
    check: (d) => d.salesThisWeek < d.salesPrevWeek * 0.7 && d.salesPrevWeek > 0,
    buildContext: (d) => ({
      conditionId: "revenue_drop_sharp",
      numbers: {
        pctDrop: Math.round(
          ((d.salesPrevWeek - d.salesThisWeek) / d.salesPrevWeek) * 100
        ),
        thisWeek: d.salesThisWeek,
        prevWeek: d.salesPrevWeek,
        topProduct: d.revenueByProduct[0]?.name,
      },
    }),
    buildPrompt: (ctx) =>
      `Revenue dropped ${ctx.numbers.pctDrop}% this week (K ${ctx.numbers.thisWeek} vs K ${ctx.numbers.prevWeek} last week). Top product last period was ${ctx.numbers.topProduct}. Write one recovery tip.`,
  },
  {
    id: "cash_runway_short",
    priority: 1,
    cooldown_days: 2,
    plans: ["free", "pro_solo", "pro_growth"],
    check: (d) => d.cashRunwayDays > 0 && d.cashRunwayDays < 21,
    buildContext: (d) => ({
      conditionId: "cash_runway_short",
      numbers: {
        days: d.cashRunwayDays,
        expenses: d.expensesThisMonth,
        topExpenseCategory: d.expensesByCategory[0]?.category,
        topExpenseAmount: d.expensesByCategory[0]?.amount,
      },
    }),
    buildPrompt: (ctx) =>
      `This business has only ${ctx.numbers.days} days of cash runway left. Monthly expenses are K ${ctx.numbers.expenses}, led by ${ctx.numbers.topExpenseCategory} at K ${ctx.numbers.topExpenseAmount}. Write one urgent cash flow tip.`,
  },
  {
    id: "single_product_dependency",
    priority: 2,
    cooldown_days: 14,
    plans: ["pro_solo", "pro_growth"],
    check: (d) =>
      d.revenueByProduct.length > 0 && d.revenueByProduct[0].pct > 60,
    buildContext: (d) => ({
      conditionId: "single_product_dependency",
      numbers: {
        productName: d.revenueByProduct[0].name,
        pct: d.revenueByProduct[0].pct,
        revenue: d.revenueByProduct[0].revenue,
      },
      productName: d.revenueByProduct[0].name,
    }),
    buildPrompt: (ctx) =>
      `${ctx.numbers.productName} makes up ${ctx.numbers.pct}% of this business's revenue (K ${ctx.numbers.revenue}). This is a high dependency risk. Write one diversification tip.`,
  },
  {
    id: "low_margin_product",
    priority: 2,
    cooldown_days: 14,
    plans: ["pro_solo", "pro_growth"],
    check: (d) =>
      d.products.some((p) => p.margin < 0.15 && p.salesLast30 > 0),
    buildContext: (d) => {
      const p = d.products
        .filter((p) => p.margin < 0.15 && p.salesLast30 > 0)
        .sort((a, b) => a.margin - b.margin)[0];
      const avgMargin =
        d.products.reduce((s, p) => s + p.margin, 0) / d.products.length;
      return {
        conditionId: "low_margin_product",
        numbers: {
          productName: p.name,
          margin: Math.round(p.margin * 100),
          avgMargin: Math.round(avgMargin * 100),
          sellingPrice: p.sellingPrice,
          buyingPrice: p.buyingPrice,
        },
        productName: p.name,
      };
    },
    buildPrompt: (ctx) =>
      `${ctx.numbers.productName} has only a ${ctx.numbers.margin}% profit margin (buying K ${ctx.numbers.buyingPrice}, selling K ${ctx.numbers.sellingPrice}). Store average is ${ctx.numbers.avgMargin}%. Write one margin improvement tip.`,
  },
  {
    id: "reorder_overdue",
    priority: 2,
    cooldown_days: 3,
    plans: ["free", "pro_solo", "pro_growth"],
    check: (d) =>
      d.products.some(
        (p) => p.stock <= p.minThreshold && p.salesLast30 > 0
      ),
    buildContext: (d) => {
      const p = d.products.filter(
        (p) => p.stock <= p.minThreshold && p.salesLast30 > 0
      )[0];
      const daysLeft =
        p.salesLast30 > 0
          ? Math.floor(p.stock / (p.salesLast30 / 30))
          : 0;
      return {
        conditionId: "reorder_overdue",
        numbers: { productName: p.name, stock: p.stock, daysLeft },
        productName: p.name,
      };
    },
    buildPrompt: (ctx) =>
      `${ctx.numbers.productName} is at or below its reorder level with only ${ctx.numbers.stock} units left — approximately ${ctx.numbers.daysLeft} days of stock remaining at current sales pace. Write one restocking tip.`,
  },
  {
    id: "expenses_spike",
    priority: 3,
    cooldown_days: 14,
    plans: ["pro_solo", "pro_growth"],
    check: (d) =>
      d.expensesThisMonth > d.expensesPrevMonth * 1.25 &&
      d.expensesPrevMonth > 0,
    buildContext: (d) => ({
      conditionId: "expenses_spike",
      numbers: {
        pctIncrease: Math.round(
          ((d.expensesThisMonth - d.expensesPrevMonth) / d.expensesPrevMonth) *
            100
        ),
        thisMonth: d.expensesThisMonth,
        prevMonth: d.expensesPrevMonth,
        topCategory: d.expensesByCategory[0]?.category,
        topAmount: d.expensesByCategory[0]?.amount,
      },
    }),
    buildPrompt: (ctx) =>
      `Expenses jumped ${ctx.numbers.pctIncrease}% this month (K ${ctx.numbers.thisMonth} vs K ${ctx.numbers.prevMonth}). The top expense category is ${ctx.numbers.topCategory} at K ${ctx.numbers.topAmount}. Write one expense review tip.`,
  },
  {
    id: "slow_movers_accumulating",
    priority: 3,
    cooldown_days: 10,
    plans: ["free", "pro_solo", "pro_growth"],
    check: (d) => d.slowMovers.length >= 3,
    buildContext: (d) => ({
      conditionId: "slow_movers_accumulating",
      numbers: {
        count: d.slowMovers.length,
        topItem: d.slowMovers[0]?.name,
        totalValue: d.deadStockValueThisMonth,
      },
    }),
    buildPrompt: (ctx) =>
      `This business has ${ctx.numbers.count} slow-moving products, with ${ctx.numbers.topItem} being the worst performer. Approximately K ${ctx.numbers.totalValue} is tied up in slow stock. Write one clearance tip.`,
  },
  {
    id: "high_velocity_product",
    priority: 3,
    cooldown_days: 7,
    plans: ["pro_solo", "pro_growth"],
    check: (d) =>
      d.products.some(
        (p) => p.salesLast30 > p.salesPrev30 * 2 && p.salesPrev30 > 0
      ),
    buildContext: (d) => {
      const p = d.products
        .filter(
          (p) => p.salesLast30 > p.salesPrev30 * 2 && p.salesPrev30 > 0
        )
        .sort((a, b) => b.salesLast30 - a.salesLast30)[0];
      const daysLeft =
        p.salesLast30 > 0
          ? Math.floor(p.stock / (p.salesLast30 / 30))
          : 99;
      return {
        conditionId: "high_velocity_product",
        numbers: {
          productName: p.name,
          daysLeft,
          salesLast30: p.salesLast30,
          salesPrev30: p.salesPrev30,
        },
        productName: p.name,
      };
    },
    buildPrompt: (ctx) =>
      `${ctx.numbers.productName} is selling twice as fast as last month (${ctx.numbers.salesLast30} units vs ${ctx.numbers.salesPrev30}). At this pace, stock runs out in ${ctx.numbers.daysLeft} days. Write one stock management tip.`,
  },
  {
    id: "no_sales_logged_today",
    priority: 4,
    cooldown_days: 1,
    plans: ["free", "pro_solo", "pro_growth"],
    check: (d) => d.salesThisWeek === 0,
    buildContext: (d) => ({
      conditionId: "no_sales_logged_today",
      numbers: { businessName: d.businessName },
    }),
    buildPrompt: () =>
      `No sales have been recorded this week. Either the shop is quiet or sales are not being logged. Write one short reminder tip about the importance of recording every sale for accurate reporting.`,
  },
  {
    id: "dominant_expense_category",
    priority: 4,
    cooldown_days: 14,
    plans: ["pro_solo", "pro_growth"],
    check: (d) => {
      if (!d.expensesThisMonth || !d.expensesByCategory.length) return false;
      return (
        d.expensesByCategory[0].amount / d.expensesThisMonth > 0.4
      );
    },
    buildContext: (d) => ({
      conditionId: "dominant_expense_category",
      numbers: {
        category: d.expensesByCategory[0].category,
        amount: d.expensesByCategory[0].amount,
        pct: Math.round(
          (d.expensesByCategory[0].amount / d.expensesThisMonth) * 100
        ),
        totalExpenses: d.expensesThisMonth,
      },
    }),
    buildPrompt: (ctx) =>
      `${ctx.numbers.category} accounts for ${ctx.numbers.pct}% of total expenses (K ${ctx.numbers.amount} of K ${ctx.numbers.totalExpenses}). For Zambian retail, this category typically runs 15–25% of expenses. Write one cost review tip.`,
  },
  {
    id: "margin_improving",
    priority: 5,
    cooldown_days: 14,
    plans: ["pro_solo", "pro_growth"],
    check: (d) =>
      d.grossMarginThisMonth > d.grossMarginPrevMonth + 5 &&
      d.grossMarginPrevMonth > 0,
    buildContext: (d) => ({
      conditionId: "margin_improving",
      numbers: {
        thisMonth: Math.round(d.grossMarginThisMonth),
        prevMonth: Math.round(d.grossMarginPrevMonth),
        improvement: Math.round(
          d.grossMarginThisMonth - d.grossMarginPrevMonth
        ),
      },
    }),
    buildPrompt: (ctx) =>
      `Gross profit margin improved from ${ctx.numbers.prevMonth}% to ${ctx.numbers.thisMonth}% this month — a ${ctx.numbers.improvement}% gain. Write one short positive reinforcement tip acknowledging this and suggesting how to sustain it.`,
  },
];

/** Condition IDs allowed on Free plan (for frontend visibility check). */
export const FREE_PLAN_CONDITION_IDS = new Set(
  TIP_CONDITIONS.filter((c) => c.plans.includes("free")).map((c) => c.id)
);

/** CTA link per condition for AITipCard. */
export const TIP_LINKS: Record<
  string,
  { label: string; path: string; category: string }
> = {
  dead_stock_rising: {
    label: "View Dead Stock",
    path: "/inventory?filter=dead_stock",
    category: "Stock",
  },
  revenue_drop_sharp: {
    label: "View Sales",
    path: "/master-ledger",
    category: "Revenue",
  },
  cash_runway_short: {
    label: "View Expenses",
    path: "/expenses",
    category: "Cash Flow",
  },
  single_product_dependency: {
    label: "View Reports",
    path: "/reports",
    category: "Revenue",
  },
  low_margin_product: {
    label: "View Inventory",
    path: "/inventory",
    category: "Stock",
  },
  reorder_overdue: {
    label: "View Inventory",
    path: "/inventory?filter=low_stock",
    category: "Stock",
  },
  expenses_spike: {
    label: "View Expenses",
    path: "/expenses",
    category: "Expenses",
  },
  slow_movers_accumulating: {
    label: "View Inventory",
    path: "/inventory?filter=slow_movers",
    category: "Stock",
  },
  high_velocity_product: {
    label: "View Inventory",
    path: "/inventory",
    category: "Stock",
  },
  no_sales_logged_today: {
    label: "Record Sale",
    path: "/manual-entry",
    category: "Revenue",
  },
  dominant_expense_category: {
    label: "View Expenses",
    path: "/expenses",
    category: "Expenses",
  },
  margin_improving: {
    label: "View Reports",
    path: "/reports",
    category: "Revenue",
  },
};
